import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import AppTrainingsRadarChart from '../../../../components/charts/app-trainings-radar-chart/AppTrainingsRadarChart';
import AppTrainingsSkillsRadarChart from '../../../../components/charts/app-trainings-skills-radar-chart/AppTrainingsSkillsRadarChart';
import PageContainer from '../../../../components/layout/page-container/PageContainer';
import { useRegisterPageContainerError } from '../../../../components/layout/page-container/page-container-error-handler';
import NoResults from '../../../../components/reusable/no-results';
import { usePullRefreshInvalidateTags } from '../../../../hooks/usePullRefreshInvalidateTags';
import { exercisesApi } from '../../../../redux/api/exercisesAPI';
import { trainingsApi } from '../../../../redux/api/trainingsAPI';
import { EXERCISE_PAGES, TRAININGS } from '../../../../redux/constants';
import TrainingListItem from '../../../trainings/trainings/training-list/training-list-item/TrainingListItem';
import StatisticsChartsSkeleton from '../shared/StatisticsChartsSkeleton';
import { useStyles } from './StatisticsDay.styles';
import { useStatisticsDay } from './useStatisticsDay';

const StatisticsDay = () => {
  const theme = useTheme();

  const { classes } = useStyles(theme);
  const { t } = useTranslation();
  const {
    dayString,
    dayTrainings,
    exercises,
    isLoadingTrainings,
    isFetchingTrainings,
    pageTitle,
    isDayTrainingsError,
    dayTrainingsError,
    isAvailableDetailedExercisesError,
    availableDetailedExercisesError,
    isTrainingExercisesDetailedError,
    trainingExercisesDetailedError,
  } = useStatisticsDay();
  const { onRefresh } = usePullRefreshInvalidateTags([
    {
      apiSlice: trainingsApi,
      tags: [TRAININGS.TRAININGS_BY_DAY, TRAININGS.EXERCISES],
    },
    {
      apiSlice: exercisesApi,
      tags: [EXERCISE_PAGES.AVAILABLE_DETAILED],
    },
  ]);

  const isChartsLoading = isLoadingTrainings || isFetchingTrainings;
  const handleRetry = useCallback(() => {
    void onRefresh();
  }, [onRefresh]);
  useRegisterPageContainerError({
    errors: [
      ...(isDayTrainingsError && dayTrainingsError ? [dayTrainingsError] : []),
      ...(isAvailableDetailedExercisesError && availableDetailedExercisesError
        ? [availableDetailedExercisesError]
        : []),
      ...(isTrainingExercisesDetailedError && trainingExercisesDetailedError
        ? [trainingExercisesDetailedError]
        : []),
    ],
    isLoading: isChartsLoading,
    hasData: dayTrainings.length > 0,
    onRetry: handleRetry,
  });

  if (!dayString) {
    return (
      <PageContainer
        backButton
        title={t('statistics.dayStatistics')}
        onRefresh={onRefresh}>
        <Box className={classes.wrapper}>
          <NoResults />
        </Box>
      </PageContainer>
    );
  }

  return (
    <PageContainer backButton title={pageTitle} onRefresh={onRefresh}>
      <Box className={classes.wrapper}>
        {isChartsLoading && <StatisticsChartsSkeleton variant="day" />}

        {!isChartsLoading && (
          <>
            {dayTrainings.length === 0 ? (
              <Box className={classes.emptyState}>
                <NoResults />
              </Box>
            ) : (
              <>
                <Box className={classes.chartsWrapper}>
                  <Box className={classes.chartSection}>
                    <Typography variant="label-text-md" marginBottom={1}>
                      {t('statistics.loadDistribution')}
                    </Typography>
                    <Box className={classes.chartContainer}>
                      <AppTrainingsRadarChart
                        trainings={dayTrainings}
                        exercises={exercises}
                      />
                    </Box>
                  </Box>
                  <Box className={classes.chartSection}>
                    <Typography variant="label-text-md" marginBottom={1}>
                      {t('statistics.skillsDistribution')}
                    </Typography>
                    <Box className={classes.chartContainer}>
                      <AppTrainingsSkillsRadarChart
                        trainings={dayTrainings}
                        exercises={exercises}
                      />
                    </Box>
                  </Box>
                </Box>
                <Box className={classes.trainingsSection}>
                  <Typography variant="label-text-md">
                    {t('statistics.trainings')} ({dayTrainings.length})
                  </Typography>
                  <Box className={classes.trainingsList}>
                    {dayTrainings.map((training) => (
                      <TrainingListItem
                        key={training.id}
                        training={training}
                        showAvatar={false}
                        showStartButton={false}
                        showEditButton={false}
                        showDeleteButton={false}
                        showCopyButton={false}
                        showShareButton={false}
                      />
                    ))}
                  </Box>
                </Box>
              </>
            )}
          </>
        )}
      </Box>
    </PageContainer>
  );
};

export default StatisticsDay;
